import React from 'react';
import { PipeObject, Token, Replacement } from '../types';
import { AddCssClassToBody } from '../../utils/add-css-class-body/add-css-class-body';
import { RenderCustomComponent } from '../../utils/render-custom-component/render-custom-component';
import { uuid } from '../../utils/uuid/uuid';

const internal = (to?: string) => to && !/\.[^\s]{2,}/.test(to);
const regex = /\[(?<title>[^\]]*)\]\((?<url>.*?)(?=\"|\))\)/i;
const regexAHrefHtmlElement = /<a href=[\\]?"(?<url>.*?)[\\]?"(?<props>.*?)>(?<title>.*?)<\/a>/i;

export type UrlLinkProps = {
  customComponentWrapper?: any;
  urlComponents?: { internal?: any; external?: any };
};

let classAdded = false;
const addCss = () => {
  if (!classAdded) {
    classAdded = AddCssClassToBody(`
    .cvna-parser-link {
      text-decoration: underline;
      color: var(--cvna-color-blue-2, #00B3D9);
    }

    .cvna-parser-link:hover {
      color: var(--cvna-color-blue-2, #00B3D9);
    }`);
  }
};

export const UrlLink = ({
  customComponentWrapper,
  urlComponents,
}: UrlLinkProps = {}) => {
  return ({ input, data, lex }: PipeObject) => {
    let newInput = input;
    // check for <a href /> links, and convert those over to components if needed
    if (urlComponents?.internal || urlComponents?.external) {
      addCss();
      let obj: Token;
      while ((obj = lex.next())) {
        if (obj.token === 'url_link') {
          const value = input.substring(obj.begin, obj.end + 1);
          const match = value.match(regexAHrefHtmlElement);
          if (match && match?.length > 0) {
            const { url, title, props } = match?.groups || {};
            const isInternalLink = internal(match?.groups?.url);
            const Component = isInternalLink
              ? urlComponents?.internal
              : urlComponents?.external;

            if (Component) {
              const id = `${url}-${title}-${uuid()}`;
              newInput = newInput.replace(value, `<span id="${id}"></span>`);
              RenderCustomComponent(
                customComponentWrapper,
                <Component url={url} title={title} props={props} />,
                id
              );
            }
          }
        }
      }
    }

    const linkMatches = newInput.match(new RegExp(regex, 'g'));
    if (linkMatches && linkMatches.length > 0) {
      addCss();

      for (const link of linkMatches) {
        if (link && link.length > 0) {
          const { groups: { title, url } = { title: '', url: '' } } =
            link.match(regex) || {};

          if (url && title) {
            const isInternalLink = internal(url);
            const Component = isInternalLink
              ? urlComponents?.internal
              : urlComponents?.external;

            if (Component) {
              const id = `${url}-${title}-${uuid()}`;
              // we have a custom component, use that one
              newInput = newInput.replace(link, `<span id="${id}"></span>`);
              RenderCustomComponent(
                customComponentWrapper,
                <Component url={url} title={title} />,
                id
              );
            } else {
              const ahref = isInternalLink
                ? `<a class="cvna-parser-link" href="${url}">${title}</a>`
                : `<a class="cvna-parser-link" href="${url}" target="_blank" rel="noopener noreferrer">${title}</a>`;
              newInput = newInput.replace(link, ahref);
            }
          }
        }
      }
    }

    return { input: newInput, data, lex };
  };
};
